/**
 * Shared utility functions for the template and proposal preview views.
 *
 * Centralises colour mapping, day-of-week labels, and ISO date/time
 * formatting so they are not duplicated across
 * multiple preview components.
 *
 * Date/time work goes through Luxon to match the rest of the monorepo
 * (`packages/auth`, `packages/common`, `packages/contracts`, etc. all
 * speak Luxon).
 */

import { DateTime } from 'luxon';

/**
 * Grouping mode for the template/proposal preview surfaces: by shift type or by
 * staff group. Shared so the preview components don't each redeclare it.
 */
export type PreviewMode = 'shift-type' | 'staff-group';

/**
 * Formats an ISO 8601 datetime string (e.g. `2026-04-02T09:00:00+00:00`)
 * as `HH:mm` for shift-pill display. The embedded offset is preserved
 * as-is — we slice the HH:mm out of the time portion directly rather
 * than going through `date-fns format`, which would convert to the
 * host's local timezone and render the wrong wall-clock time on a
 * server in a different region.
 *
 * Rotagen emits proposal shifts in the site's local time; reading the
 * time component verbatim keeps the displayed value aligned with that.
 */
export function formatTime(isoDatetime: string): string {
  const timePart = isoDatetime.split('T')[1] ?? '';
  return timePart.slice(0, 5);
}

/**
 * Formats a `YYYY-MM-DD` date string as a short day-of-week label
 * (e.g. `Thu`). Used for the day column headers in the proposal-preview
 * grid.
 *
 * Locale-fixed today — `toFormat('ccc')` resolves against Luxon's
 * default locale (typically English in our runtime). The Phase 6
 * Lingui retrofit (#97) will need to thread the active i18n locale
 * through via `setLocale(...)` or a `.setLocale()` chain; flagging
 * here so the retrofit doesn't miss it.
 */
export function formatDayShort(dateString: string): string {
  return DateTime.fromISO(dateString).toFormat('ccc');
}

/**
 * Formats a `YYYY-MM-DD` date string as `d MMM` (e.g. `2 Apr`). Used
 * directly under the day-short label in the proposal-preview grid so
 * users see both the day name and the calendar date.
 *
 * Locale-fixed (see `formatDayShort` for the same caveat re: #97).
 */
export function formatDayFull(dateString: string): string {
  return DateTime.fromISO(dateString).toFormat('d LLL');
}

/**
 * Working-time duration in hours between two ISO 8601 datetimes.
 * Returns a float — caller decides whether to round / `toFixed`.
 *
 * Luxon's `diff` walks across day boundaries correctly, so overnight
 * shifts (e.g. 22:00 → 06:00 next day) produce the expected ~8h
 * without sign flips.
 */
export function calculateShiftHours(start: string, end: string): number {
  return DateTime.fromISO(end).diff(DateTime.fromISO(start), 'hours').hours;
}

/**
 * Renders a numeric hours value for the day-column header — integer
 * when whole (`8`), one decimal place otherwise (`8.5`). Pulled out
 * so the proposal-preview header doesn't carry the toFixed branch
 * inline.
 */
export function formatHoursDisplay(hours: number): string {
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
}
